import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Plus, Trash2, Mail } from 'lucide-react';
import FieldAssignmentModal from '../components/FieldAssignmentModal';
import RecipientRoleSelect from '../components/RecipientRoleSelect';

const PrepareEnvelope = () => {
  const { accessToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { envelopeId } = useParams();
  
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const [envelope, setEnvelope] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [recipients, setRecipients] = useState([
    { name: '', email: '', role: 'Signer', routing_order: 1 }
  ]);
  const [emailMessage, setEmailMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [fields, setFields] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  
  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentUrl]);

  // Load envelope data
  useEffect(() => {
    const loadEnvelope = async () => {
      try {
        setIsLoading(true);
        
        // Fetch envelope details
        const envelopeResponse = await axios.get(`${API}/envelopes/${envelopeId}`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        setEnvelope(envelopeResponse.data);
        
        // Load existing recipients if any
        if (envelopeResponse.data.recipients && envelopeResponse.data.recipients.length > 0) {
          setRecipients(envelopeResponse.data.recipients);
        }
        
        // Fetch fields for this envelope
        try {
          const fieldsResponse = await axios.get(`${API}/envelopes/${envelopeId}/fields`, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          setFields(fieldsResponse.data || []);
        } catch (error) {
          console.error('Failed to load fields:', error);
          setFields([]);
        }
        
        // Fetch document for preview
        const docsResponse = await axios.get(`${API}/envelopes/${envelopeId}/docs`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        
        if (docsResponse.data && docsResponse.data.length > 0) {
          const fileId = docsResponse.data[0].file_id;
          // Fetch the file with auth and create blob URL for preview
          try {
            const fileResponse = await axios.get(`${API}/files/${fileId}`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              responseType: 'blob'
            });
            const blobUrl = URL.createObjectURL(fileResponse.data);
            setDocumentUrl(blobUrl);
          } catch (fileError) {
            console.error('Failed to load document preview:', fileError);
            // Document preview will show placeholder if this fails
          }
        }
        
      } catch (error) {
        console.error('Failed to load envelope:', error);
        toast.error('Failed to load envelope details');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (envelopeId && accessToken) {
      loadEnvelope();
    }
  }, [envelopeId, accessToken, API]);

  const addRecipient = () => {
    const maxOrder = Math.max(...recipients.map(r => r.routing_order), 0);
    setRecipients([...recipients, {
      name: '',
      email: '',
      role: 'Signer',
      routing_order: maxOrder + 1
    }]);
  };

  const removeRecipient = (index) => {
    if (recipients.length === 1) {
      toast.error('At least one recipient is required');
      return;
    }
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const handleSaveAndSend = async () => {
    // Validate recipients
    const validRecipients = recipients.filter(r => r.name && r.email);
    if (validRecipients.length === 0) {
      toast.error('Please add at least one recipient with name and email');
      return;
    }

    // Check if we need to show assignment modal (2+ recipients with fields)
    if (validRecipients.length >= 2 && fields.length > 0) {
      // Check if any fields are unassigned
      const unassignedFields = fields.filter(f => !f.properties?.assignedTo);
      
      if (unassignedFields.length > 0) {
        // Show assignment modal
        setShowAssignmentModal(true);
        return;
      }
    }

    // Continue with normal send flow
    await performSend(validRecipients);
  };

  const performSend = async (validRecipients) => {
    try {
      setIsSending(true);
      
      // Delete existing recipients first to avoid duplicates
      try {
        await axios.delete(
          `${API}/envelopes/${envelopeId}/recipients`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      } catch (error) {
        // Ignore if no recipients exist
        console.log('No existing recipients to delete');
      }
      
      // Save new recipients to envelope
      await axios.post(
        `${API}/envelopes/${envelopeId}/recipients`,
        { recipients: validRecipients },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      // Send envelope immediately
      await axios.post(
        `${API}/envelopes/${envelopeId}/send`,
        { message: emailMessage },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      toast.success('Document sent successfully!');
      
      // Navigate to In-Progress tab
      navigate('/envelopes?tab=in-progress');
      
    } catch (error) {
      console.error('Failed to send envelope:', error);
      toast.error(error.response?.data?.detail || 'Failed to send envelope');
    } finally {
      setIsSending(false);
    }
  };

  const handleFieldAssignment = async (assignments) => {
    try {
      setIsSending(true);
      
      // Update fields with assignments
      const updatedFields = fields.map(field => ({
        ...field,
        type: field.field_type || field.type,  // Preserve field type (backend uses field_type)
        properties: {
          ...field.properties,
          assignedTo: assignments[field.id] || ''
        },
        props: {
          ...field.props,
          assignedTo: assignments[field.id] || ''
        }
      }));
      
      // Save updated fields with assignments
      await axios.post(
        `${API}/envelopes/${envelopeId}/fields`,
        { fields: updatedFields },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      setShowAssignmentModal(false);
      
      // Continue with send
      const validRecipients = recipients.filter(r => r.name && r.email);
      await performSend(validRecipients);
      
    } catch (error) {
      console.error('Assignment failed:', error);
      toast.error('Failed to assign fields');
      setIsSending(false);
    }
  };

  const handleBackToEditor = () => {
    navigate('/send/editor', {
      state: {
        envelopeId,
        documentTitle: envelope?.title
      }
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading envelope...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Field Assignment Modal */}
      {showAssignmentModal && (
        <FieldAssignmentModal
          fields={fields}
          recipients={recipients.filter(r => r.name && r.email)}
          onAssign={handleFieldAssignment}
          onCancel={() => setShowAssignmentModal(false)}
        />
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{envelope?.title}</h1>
              <p className="text-sm text-gray-500 mt-1">Prepare your document for signing</p>
            </div>
            <button
              onClick={handleBackToEditor}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ← Back to Editor
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Document Preview */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Document Preview</h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[500px] flex items-center justify-center">
              {documentUrl ? (
                <iframe
                  src={documentUrl}
                  className="w-full h-[500px] border-0"
                  title="Document Preview"
                />
              ) : (
                <div className="text-center text-gray-500">
                  <p>Document preview not available</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Recipients & Settings */}
          <div className="space-y-6">
            {/* Recipients Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recipients</h2>
                <button
                  onClick={addRecipient}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md"
                >
                  <Plus size={16} />
                  Add Recipient
                </button>
              </div>

              <div className="space-y-4">
                {recipients.map((recipient, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        Recipient {index + 1}
                      </span>
                      {recipients.length > 1 && (
                        <button
                          onClick={() => removeRecipient(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={recipient.name}
                          onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                          placeholder="Enter recipient name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={recipient.email}
                          onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                          placeholder="recipient@example.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Role
                        </label>
                        <RecipientRoleSelect
                          value={recipient.role}
                          onChange={(newRole) => updateRecipient(index, 'role', newRole)}
                          isTemplateFlow={false}
                          recipients={recipients}
                          currentIndex={index}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Signing Order
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={recipient.routing_order}
                          onChange={(e) => updateRecipient(index, 'routing_order', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Email Message Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail size={20} className="text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Email Message (Optional)</h2>
              </div>
              
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Add a personal message to include in the email notification..."
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-2">
                This message will be included in the email sent to recipients
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleSaveAndSend}
                disabled={isSending}
                className="flex-1 px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSending ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    Send Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrepareEnvelope;
